"use client";

import * as React from "react";
import { motion } from "motion/react";
import { Mail } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { springs, transitions } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { TagInput, type TagInputProps } from "./TagInput";

/**
 * EmailChipInput — validated, bulk-paste email entry that renders each address
 * as a removable chip.
 *
 * A thin composer over `TagInput`: the chip markup, Enter-to-add,
 * Backspace-on-empty-pops-the-last, exact-match dedup, `focus-within` ring,
 * `role="combobox"` + listbox ARIA, and the forwarded `ref` to the inner
 * `<input>` all live in `TagInput` so the keyboard + a11y model stays in one
 * place. EmailChipInput adds the three things email entry needs on top:
 *
 *   1. A validation gate, every candidate is checked (default regex
 *      `^[^\s@]+@[^\s@]+\.[^\s@]+$`) before it becomes a chip, invalid entries
 *      are rejected and reported, never added.
 *   2. Bulk-paste splitting, pasting `a@x.com, b@y.com\n c@z.com` splits on
 *      `/[,\n\s]+/`, normalizes, validates, dedups, and commits every valid
 *      address in a single gesture.
 *   3. Invalid-entry surfacing, a headless `onInvalid(reason, value)` callback
 *      (the seam that replaces the donor screens' direct `toast.error`) plus an
 *      opt-in inline error string. The primitive owns no toast channel.
 *
 * Ported from two `fe-react` donors (onboarding `InviteTeammatesStep` and the
 * Creator-Studio wizard `DefineCollaboration`) and reconciled: addresses
 * normalize to lowercase by default (the dedup-correct behavior the two donors
 * disagreed on), errors go through a callback instead of a toast, and every
 * hardcoded navy / gray / green color becomes a brand token.
 *
 * Controlled only: `value` is the current address list, `onChange` receives the
 * next list on every add / paste / remove. The forwarded `ref` reaches the
 * inner `<input>`.
 *
 * @example
 *   const [emails, setEmails] = React.useState<string[]>([]);
 *   <EmailChipInput
 *     label="Invite teammates"
 *     value={emails}
 *     onChange={setEmails}
 *     max={50}
 *     showInlineError
 *   />
 */

/** Why a chip was rejected, passed to `onInvalid`. */
export type EmailChipInvalidReason = "invalid" | "duplicate" | "max";

/** Inline-error copy keyed by rejection reason. */
export interface EmailChipErrorMessages {
  invalid: string;
  duplicate: string;
  max: string;
}

const DEFAULT_ERROR_MESSAGES: EmailChipErrorMessages = {
  invalid: "Please enter a valid email address.",
  duplicate: "That email has already been added.",
  // No em-dash, kept as two clauses joined by a comma.
  max: "You have reached the maximum number of emails.",
};

/** The donor regex, simple, synchronous, no library. */
const DEFAULT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Splits a bulk-pasted blob on commas, whitespace, and newlines. */
const SPLIT_PATTERN = /[,\n\s]+/;

const DEFAULT_HINT = "Tip: you can paste multiple emails separated by commas.";

/**
 * Field sizing, mirrors `TagInput`'s metrics so the two read as siblings.
 * Applied to `TagInput` via its root `className`, the descendant selectors
 * reach the inner field box and `<input>` that `TagInput` renders.
 */
const sizeVariants = cva("", {
  variants: {
    size: {
      sm: "[&_[data-slot=tag-input-field]]:min-h-9 [&_[data-slot=tag-input-field]]:text-xs [&_input]:text-xs",
      md: "",
    },
  },
  defaultVariants: { size: "md" },
});

export interface EmailChipInputProps
  extends Omit<
      TagInputProps,
      | "value"
      | "onChange"
      | "onInvalid"
      | "suggestions"
      | "maxSuggestions"
      | "placeholder"
      | "label"
    >,
    VariantProps<typeof sizeVariants> {
  /** Field label rendered above the control via the `<Label>` primitive. */
  label?: React.ReactNode;
  /** Current emails (controlled). */
  value: readonly string[];
  /** Receives the next email list on add / paste / remove. */
  onChange: (emails: string[]) => void;
  /**
   * Per-address validity gate. Defaults to the donor regex
   * `^[^\s@]+@[^\s@]+\.[^\s@]+$`. Receives the already-normalized value.
   */
  validate?: (email: string) => boolean;
  /**
   * Case handling before validate / store / dedup. `"lowercase"` (default) is
   * the dedup-correct behavior, pass `"none"` to preserve case.
   */
  normalize?: "lowercase" | "none";
  /** Soft cap on the number of emails. Default unlimited. */
  max?: number;
  /**
   * Headless rejection callback, fires once per rejected entry. This is the
   * seam that replaces the donors' direct `toast.error`, the consumer decides
   * whether to toast, log, or ignore.
   */
  onInvalid?: (reason: EmailChipInvalidReason, value: string) => void;
  /** Render the built-in inline error string. Default `false` (stays headless). */
  showInlineError?: boolean;
  /** Override the inline-error copy per reason. */
  errorMessages?: Partial<EmailChipErrorMessages>;
  /**
   * Helper text under the field. Defaults to the paste tip, pass `null` to
   * hide it.
   */
  hint?: React.ReactNode;
  /** Input placeholder, shown while no emails exist. */
  placeholder?: string;
  /** Disable the whole control. */
  disabled?: boolean;
}

export const EmailChipInput = React.forwardRef<HTMLInputElement, EmailChipInputProps>(
  (
    {
      label = "Email addresses",
      value,
      onChange,
      validate,
      normalize = "lowercase",
      max,
      onInvalid,
      showInlineError = false,
      errorMessages,
      hint = DEFAULT_HINT,
      placeholder = "colleague@company.com",
      disabled = false,
      size,
      className,
      id,
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    // Treat `null` (not yet resolved) as "allow motion" to avoid a mount flash.
    const animate = reduced !== true;

    const autoId = React.useId();
    const fieldId = id ?? `email-chip-input-${autoId}`;
    const errorId = `${fieldId}-error`;

    const [error, setError] = React.useState<EmailChipInvalidReason | null>(null);
    // A monotonically-rising key so a fresh rejection always re-triggers the
    // shake even when the reason is unchanged (e.g. two invalid entries).
    const [shakeKey, setShakeKey] = React.useState(0);

    const isValid = React.useCallback(
      (email: string) => (validate ?? ((e: string) => DEFAULT_EMAIL_PATTERN.test(e)))(email),
      [validate],
    );

    const normalizeEmail = React.useCallback(
      (raw: string) => {
        const trimmed = raw.trim();
        return normalize === "lowercase" ? trimmed.toLowerCase() : trimmed;
      },
      [normalize],
    );

    const reject = React.useCallback(
      (reason: EmailChipInvalidReason, raw: string) => {
        onInvalid?.(reason, raw);
        setError(reason);
        setShakeKey((k) => k + 1);
      },
      [onInvalid],
    );

    /**
     * Commit one or more candidate addresses, normalizing, validating,
     * deduping (against the current value and within the batch), and honoring
     * the `max` cap. Returns nothing, calls `onChange` with the accepted set
     * and `reject` for the first failure of each kind.
     */
    const commit = React.useCallback(
      (candidates: string[]) => {
        const accepted: string[] = [];
        // Track seen across the existing value + this batch for dedup.
        const seen = new Set(value);
        let rejected = false;

        for (const candidate of candidates) {
          const email = normalizeEmail(candidate);
          if (!email) continue; // silent no-op on blanks

          if (!isValid(email)) {
            if (!rejected) reject("invalid", candidate);
            rejected = true;
            continue;
          }
          if (seen.has(email)) {
            if (!rejected) reject("duplicate", candidate);
            rejected = true;
            continue;
          }
          if (typeof max === "number" && value.length + accepted.length >= max) {
            if (!rejected) reject("max", candidate);
            rejected = true;
            continue;
          }
          seen.add(email);
          accepted.push(email);
        }

        if (accepted.length > 0) {
          if (!rejected) setError(null);
          onChange([...value, ...accepted]);
        }
      },
      [value, normalizeEmail, isValid, max, reject, onChange],
    );

    /**
     * Bridge `TagInput`'s `onChange`, the gate for every add that TagInput
     * routes through. A shorter array is a removal (always valid, passed
     * straight through). A longer array is a single Enter add, the appended
     * raw value is normalized, validated, deduped, and max-checked here instead
     * of being committed raw, so invalid / over-cap / case-variant-duplicate
     * entries are rejected through `onInvalid` rather than becoming chips.
     */
    const handleTagChange = (next: string[]) => {
      if (next.length <= value.length) {
        setError(null);
        onChange(next);
        return;
      }
      commit([next[next.length - 1]]);
    };

    /**
     * Catch the one add TagInput decides on its own, an exact-match duplicate
     * of an existing chip, which TagInput silently swallows before `onChange`
     * fires. Detect that case in the capture phase and surface it through
     * `onInvalid` so no duplicate goes unreported. We deliberately do NOT
     * `preventDefault` here, TagInput still clears its own field and drops the
     * duplicate (the correct end state). Every other add (valid, invalid,
     * over-cap, case-variant duplicate) reaches the gate via `handleTagChange`,
     * so this never double-fires.
     */
    const handleKeyDownCapture = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter") return;
      const raw = (event.target as HTMLInputElement | null)?.value ?? "";
      const trimmed = raw.trim();
      // Mirror TagInput's exact-match condition: a trimmed value already in the
      // list is the entry TagInput will swallow.
      if (trimmed && value.includes(trimmed)) {
        reject("duplicate", trimmed);
      }
    };

    /**
     * Bulk-paste, split the clipboard text, validate, dedup, and commit every
     * valid address in one gesture. Prevent default so the raw blob never
     * lands in `TagInput`'s `<input>`.
     */
    const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
      const text = event.clipboardData.getData("text");
      if (!text || !SPLIT_PATTERN.test(text)) return; // single token, let TagInput handle it
      event.preventDefault();
      commit(text.split(SPLIT_PATTERN));
    };

    const resolvedErrorMessages = { ...DEFAULT_ERROR_MESSAGES, ...errorMessages };
    const showError = showInlineError && error !== null;
    const errorText = error ? resolvedErrorMessages[error] : null;

    return (
      <div className={cn("w-full", disabled && "pointer-events-none opacity-50", className)}>
        <motion.div
          // A rejected entry gives a single-oscillation shake to signal the
          // rejection without a toast. Reduced-motion users get no shake.
          key={shakeKey}
          animate={animate && shakeKey > 0 ? { x: [0, -4, 4, -2, 0] } : { x: 0 }}
          transition={animate ? springs.snappy : { duration: 0 }}
        >
          <TagInput
            ref={ref}
            id={fieldId}
            label={label}
            value={value}
            onChange={handleTagChange}
            onPaste={handlePaste}
            onKeyDownCapture={handleKeyDownCapture}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={showError || undefined}
            aria-describedby={showError ? errorId : undefined}
            className={cn(
              sizeVariants({ size }),
              // Error-state border morph, idle --ring focus -> --destructive.
              // Token-driven, layered over TagInput's own focus-within ring.
              error !== null &&
                "[&_[data-slot=tag-input-field]]:border-destructive [&_[data-slot=tag-input-field]]:focus-within:border-destructive [&_[data-slot=tag-input-field]]:focus-within:ring-destructive",
              "[&_[data-slot=tag-input-field]]:transition-colors [&_[data-slot=tag-input-field]]:duration-[240ms] [&_[data-slot=tag-input-field]]:ease-smooth-out",
            )}
            {...rest}
          />
        </motion.div>

        {showError ? (
          <motion.p
            id={errorId}
            role="alert"
            aria-live="polite"
            className="mt-1.5 text-xs text-destructive"
            initial={animate ? { opacity: 0, y: -2 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={animate ? transitions.surface : { duration: 0 }}
          >
            {errorText}
          </motion.p>
        ) : hint ? (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Mail className="size-3.5 shrink-0" aria-hidden />
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
EmailChipInput.displayName = "EmailChipInput";
